import { Input } from "@repo/ui/components/ui/input";
import Icon from "@repo/ui/components/ui/icon/icon";
import { useTranslations } from "next-intl";
import {
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  Fragment,
  useEffect,
  useState,
} from "react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@repo/ui/components/ui/form";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@repo/ui/components/ui/accordion";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cn } from "@repo/ui/lib/utils";
import { useBrand } from "@/data/brand";
import { Skeleton } from "@repo/ui/components/ui/skeleton";
import { useAutoSubmit } from "@/hooks/useAutoSubmit";
import { ScrollArea } from "@repo/ui/components/ui/scroll-area";
import { Checkbox } from "@repo/ui/components/ui/checkbox";

const FormSchema = z.object({
  filterTerm: z.string().optional(),
  brand: z.array(z.string()).default([]),
  // .refine((value) => value.some((item) => item), {})
  // .optional(),
});

type BrandPayload = z.infer<typeof FormSchema>;

export const FiltersMenuItemBrand = ({
  applyFilter,
  initialValue,
}: {
  applyFilter: (data: { brand: string[] }) => void;
  initialValue: string[];
}) => {
  const t = useTranslations();

  const [textFilter, setTextFilter] = useState<string>("");

  const { data: brand, isLoading } = useBrand();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      brand: [],
    },
  });

  const filteredBrands = textFilter
    ? brand?.filter((b: string) =>
        b.toLowerCase().includes(textFilter.toLowerCase()),
      )
    : brand;

  function onApplyFilter(data: BrandPayload) {
    applyFilter({ brand: data?.brand });
  }

  useEffect(() => {
    if (initialValue) {
      form.setValue("brand", initialValue);
    }
  }, []);

  useAutoSubmit({
    trigger: form.trigger,
    watch: form.watch,
    onSubmit: form.handleSubmit(onApplyFilter),
  });

  const brandBlock = (brand: string, i: number, borderTop: boolean) => {
    return (
      <Fragment key={`${brand}-${i}`}>
        <FormField
          key={brand}
          control={form.control}
          name="brand"
          render={({ field }) => {
            return (
              <FormItem key={brand} className="mb-2">
                <FormControl>
                  <FilterMenuItemBrandCheckbox
                    className="*:*:font-bold"
                    brand={brand}
                    checked={field.value?.includes(brand)}
                    onCheckedChange={(checked) => {
                      return checked
                        ? field.onChange([...field.value, brand])
                        : field.onChange(
                            field.value?.filter((value) => value !== brand),
                          );
                    }}
                  />
                </FormControl>
              </FormItem>
            );
          }}
        />
      </Fragment>
    );
  };

  const titledList = (filteredBrands: string[]) => {
    if (isLoading) {
      return (
        <div className="flex flex-col gap-4 px-5">
          <Skeleton className="h-[40px] w-full rounded-full" />
          <Skeleton className="h-[40px] w-full rounded-full" />
          <Skeleton className="h-[40px] w-full rounded-full" />
          <Skeleton className="h-[40px] w-full rounded-full" />
        </div>
      );
    }

    return filteredBrands
      ? filteredBrands
          .sort((a: string, b: string) => a.localeCompare(b))
          .map((brand: string, i) => {
            return brandBlock(brand, i, true);
          })
      : [];
  };

  return (
    <Accordion type="single" collapsible>
      <AccordionItem value="item-1">
        <div className="py-[16px]">
          <AccordionTrigger className="max-md:text-base font-medium py-0 data-[state=open]:text-primary-magenta">
            {t("Products-list.filters.brand")}
          </AccordionTrigger>
          <AccordionContent className="mt-[20px] p-0">
            <ScrollArea className="h-[400px]">
              <div className="max-md:w-[100vw] pb-6 text-primary-black dark:text-primary-white">
                <form
                  id="search-form"
                  className="mb-4 pl-1"
                  onSubmit={(e) => {
                    e.preventDefault();
                  }}
                >
                  <Input
                    rounded
                    slim
                    icon="Search"
                    tabIndex={2}
                    id="filter"
                    name="filter"
                    label={t("Products-list.filters.brand-placeholder")}
                    /* type="search"*/
                    onChange={(e) => setTextFilter(e.target.value)}
                    value={textFilter}
                    className="w-[300px] max-md:w-[79%]"
                  />
                </form>
                {
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onApplyFilter)}>
                      {titledList(filteredBrands)}
                    </form>
                  </Form>
                }
              </div>
            </ScrollArea>
          </AccordionContent>
        </div>
      </AccordionItem>
    </Accordion>
  );
};

type FilterMenuItemBrandCheckboxProps = {
  brand?: string;
  checked: boolean;
} & ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>;

const FilterMenuItemBrandCheckbox = forwardRef<
  ElementRef<typeof CheckboxPrimitive.Root>,
  FilterMenuItemBrandCheckboxProps
>(({ className, brand, checked, ...props }, ref) => {
  return (
    <div className={cn("*:text-[14px]", { "font-bold": checked })}>
      <Checkbox {...props}>{brand}</Checkbox>
    </div>
  );
});
